import { EPostStatus } from '@constants';
import { IDate, IPost } from '@sdk/contracts';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { usePostForm } from '@/hooks';
import { useMutatePost } from '@/store';

export const usePostFormLogic = () => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const { createPost, updatePost, isPending } = useMutatePost();
  const { isOpen, post, setPost, setIsOpen } = usePostForm();
  const [postForm] = Form.useForm();
  const [scheduleForm] = Form.useForm();

  const isEditingScheduledPost = post?.status === EPostStatus.SCHEDULED;

  const closeModal = () => {
    setPost();
    setIsOpen(false);
    postForm.resetFields();
    scheduleForm.resetFields();
    setShowScheduleModal(false);
  };

  const handlePostFormSubmit = async (values: IPost) => {
    const finalStatus = values.status || EPostStatus.PUBLISHED;
    let scheduledDate = values.scheduledPublishDate;

    if (post?._id && post.status === EPostStatus.SCHEDULED) {
      scheduledDate = scheduledDate || post.scheduledPublishDate;
    }

    const postData = {
      ...values,
      _id: post?._id,
      tags: values.tags || [],
      status: finalStatus,
      scheduledPublishDate: scheduledDate
    };

    if (post?._id) {
      updatePost(postData, { onSuccess: closeModal });
    } else {
      createPost(postData, { onSuccess: closeModal });
    }
  };

  const saveAsDraft = async () => {
    const values = postForm.getFieldsValue();

    const postData: Partial<IPost> & { status: EPostStatus; tags: string[] } = {
      _id: post?._id,
      status: EPostStatus.DRAFT,
      tags: values.tags || []
    };

    if (values.title !== undefined) postData.title = values.title;
    if (values.content !== undefined) postData.content = values.content;

    if (values.topic !== undefined) {
      postData.topic = typeof values.topic === 'string' ? values.topic : values.topic?._id;
    }

    if (values.featuredImage !== undefined) {
      postData.featuredImage =
        typeof values.featuredImage === 'string' ? values.featuredImage : values.featuredImage?._id;
    }

    if (values.isFeatured !== undefined) postData.isFeatured = values.isFeatured;
    if (values.sendEmailNotification !== undefined) {
      postData.sendEmailNotification = values.sendEmailNotification;
    }

    const mutationFn = post?._id ? updatePost : createPost;
    mutationFn(postData, { onSuccess: closeModal });
  };

  const publishNow = async () => {
    await postForm.validateFields(['title', 'topic', 'featuredImage', 'content']);

    const values = postForm.getFieldsValue();

    const postData = {
      ...values,
      tags: values.tags || [],
      status: EPostStatus.PUBLISHED,
      scheduledPublishDate: {
        dateTime: dayjs().tz('Europe/Madrid').format('YYYY-MM-DDTHH:mm:ss'),
        timeZone: 'Europe/Madrid'
      }
    };

    if (post?._id) {
      updatePost({ ...postData, _id: post._id }, { onSuccess: closeModal });
    } else {
      createPost(postData, { onSuccess: closeModal });
    }
  };

  const openScheduleModal = async () => {
    await postForm.validateFields(['title', 'topic', 'featuredImage', 'content']);
    setShowScheduleModal(true);
  };

  const handleScheduleSubmit = async (values: IDate) => {
    const scheduledPublishDate: IDate = {
      dateTime: dayjs.tz(values.dateTime, values.timeZone).format('YYYY-MM-DDTHH:mm:ss'),
      timeZone: values.timeZone
    };

    if (isEditingScheduledPost) {
      postForm.setFieldValue('scheduledPublishDate', scheduledPublishDate);
      setShowScheduleModal(false);
      return;
    }

    const postFormValues = await postForm.getFieldsValue();
    const postData = {
      ...postFormValues,
      _id: post?._id,
      tags: postFormValues.tags || [],
      status: EPostStatus.SCHEDULED,
      scheduledPublishDate
    };

    if (post?._id) {
      updatePost(postData, {
        onSuccess: () => {
          closeModal();
          setShowScheduleModal(false);
        }
      });
    } else {
      createPost(postData, {
        onSuccess: () => {
          closeModal();
          setShowScheduleModal(false);
        }
      });
    }
  };

  useEffect(() => {
    if (post) {
      postForm.setFieldsValue({
        ...post,
        topic: post.topic?._id
      });
    }
  }, [post, postForm]);

  useEffect(() => {
    return () => {
      closeModal();
    };
  }, []);

  useEffect(() => {
    if (!showScheduleModal) {
      scheduleForm.resetFields();
      return;
    }

    const scheduledDate =
      postForm.getFieldValue('scheduledPublishDate') ?? post?.scheduledPublishDate;

    const dateTime = scheduledDate?.dateTime
      ? dayjs.tz(scheduledDate.dateTime, scheduledDate.timeZone)
      : dayjs();
    const timeZone = scheduledDate?.timeZone || 'Europe/Madrid';

    scheduleForm.setFieldsValue({ dateTime, timeZone });
  }, [showScheduleModal, post, postForm, scheduleForm]);

  return {
    isOpen,
    post,
    showScheduleModal,
    isEditingScheduledPost,
    isPending,
    postForm,
    scheduleForm,
    closeModal,
    handlePostFormSubmit,
    saveAsDraft,
    publishNow,
    openScheduleModal,
    handleScheduleSubmit,
    setShowScheduleModal
  };
};
